import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, IdCard, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

// Alta obligatoria de datos de RH: bloquea la navegación hasta completar el expediente.
export default function OnboardingGate({ onReady }) {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/people/onboarding/status").then((r) => {
      setStatus(r.data);
      setForm(r.data.values || {});
      if (r.data.complete) onReady?.();
    }).catch(() => onReady?.());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!status || status.complete) return null;

  const fields = status.fields || [];
  const invalid = fields.filter((f) => !String(form[f.key] || "").trim());

  const submit = async () => {
    if (invalid.length) {
      toast.error("Completa todos los campos obligatorios");
      return;
    }
    setBusy(true);
    try {
      await api.post("/people/onboarding/submit", form);
      toast.success("Expediente completado. ¡Bienvenido!");
      setStatus({ ...status, complete: true });
      onReady?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-navy/70 backdrop-blur-md flex items-center justify-center p-4" data-testid="onboarding-gate">
      <motion.div initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
        className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto thin-scroll p-8 shadow-2xl">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#6C5CE7] to-[#3d2fae] flex items-center justify-center">
            <IdCard className="w-5.5 h-5.5 text-white" style={{ width: 22, height: 22 }} />
          </div>
          <div>
            <h2 className="font-editorial text-2xl tracking-tight">Completa tu expediente</h2>
            <p className="text-sm text-navy/50">Recursos Humanos necesita estos datos para activar tu cuenta. Son obligatorios.</p>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-x-4 gap-y-3 mt-6">
          {fields.map((f) => (
            <div key={f.key} className={["nombre", "apellido"].includes(f.key) ? "" : ""}>
              <label className="text-xs font-medium text-navy/55 mb-1 block">
                {f.label} <span className="text-rose-500">*</span>
              </label>
              <input type={f.type} value={form[f.key] || ""} data-testid={`onboarding-field-${f.key}`}
                onChange={(e) => setForm((x) => ({ ...x, [f.key]: e.target.value }))}
                className={`field w-full ${!String(form[f.key] || "").trim() ? "border-rose-300/60" : ""}`} />
            </div>
          ))}
        </div>
        <button onClick={submit} disabled={busy} data-testid="onboarding-submit"
          className="mt-7 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-navy text-cream py-3 text-sm font-medium hover:bg-[#6C5CE7] transition-colors disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Guardar y entrar a OMNIA <ArrowRight className="w-4 h-4" /></>}
        </button>
        {invalid.length > 0 && (
          <div className="mt-2 text-center text-[11px] text-navy/40">{invalid.length} campo(s) por completar</div>
        )}
      </motion.div>
    </div>
  );
}
